<template>
  <div class="sa-notify-host" role="region" aria-label="Notifications">
    <!-- warning / danger: must interrupt -->
    <div aria-live="assertive">
      <div
        v-for="n in alerts"
        :key="n.id"
        :class="['sa-card', 'sa-' + n.uiState]"
        role="alert"
        @click="dismiss(n.id)"
      >
        <span class="sa-icon" aria-hidden="true" v-html="iconFor(n.uiState)" />
        <div class="sa-body">
          <p class="sa-title">
            {{ n.title }}
            <span v-if="n.count > 1" class="sa-count">&times;{{ n.count }}</span>
          </p>
          <p v-if="n.detail" class="sa-detail">{{ n.detail }}</p>
          <p v-if="n.next" class="sa-next">{{ n.next }}</p>
        </div>
        <button
          type="button"
          class="sa-dismiss"
          aria-label="Dismiss"
          @click.stop="dismiss(n.id)"
        >
          &times;
        </button>
      </div>
    </div>

    <!-- success / busy / idle: informational -->
    <div aria-live="polite">
      <div
        v-for="n in statuses"
        :key="n.id"
        :class="['sa-card', 'sa-' + n.uiState]"
        role="status"
        @click="dismiss(n.id)"
      >
        <span class="sa-icon" aria-hidden="true" v-html="iconFor(n.uiState)" />
        <div class="sa-body">
          <p class="sa-title">
            {{ n.title }}
            <span v-if="n.count > 1" class="sa-count">&times;{{ n.count }}</span>
          </p>
          <p v-if="n.detail" class="sa-detail">{{ n.detail }}</p>
          <p v-if="n.next" class="sa-next">{{ n.next }}</p>
        </div>
        <button
          type="button"
          class="sa-dismiss"
          aria-label="Dismiss"
          @click.stop="dismiss(n.id)"
        >
          &times;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// The single notification surface for the app. See DESIGN.md section 5.
// Deliberately carries no Tailwind classes: public/assets/styles.css is a
// committed, pre-compiled artefact and several classes this card would want
// are not in it.

const ICONS = {
  warning:
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
  danger:
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
  success:
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
  busy: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M21 12a9 9 0 1 1-6.22-8.56"/></svg>',
  idle: "",
};

export default {
  name: "AppNotifications",
  data() {
    return { successTimers: {} };
  },
  computed: {
    notifications() {
      return this.$store.state.notifications;
    },
    alerts() {
      return this.notifications.filter(
        (n) => n.uiState == "warning" || n.uiState == "danger"
      );
    },
    statuses() {
      return this.notifications.filter(
        (n) => n.uiState != "warning" && n.uiState != "danger"
      );
    },
  },
  watch: {
    // Only success self-clears. warning and danger persist until dismissed,
    // including across route changes (DESIGN.md section 5, "Timing").
    notifications: {
      handler(list) {
        list.forEach((n) => {
          if (n.uiState != "success" || this.successTimers[n.id]) return;
          this.successTimers[n.id] = setTimeout(() => {
            delete this.successTimers[n.id];
            this.dismiss(n.id);
          }, 1500);
        });
      },
      deep: true,
    },
  },
  methods: {
    iconFor(uiState) {
      return ICONS[uiState] || "";
    },
    dismiss(id) {
      this.$store.commit("DISMISS_NOTIFICATION", id);
    },
    onKeydown(ev) {
      if (ev.key != "Escape") return;
      const newest = this.notifications[0];
      if (newest) this.dismiss(newest.id);
    },
  },
  mounted() {
    document.addEventListener("keydown", this.onKeydown);
  },
  unmounted() {
    document.removeEventListener("keydown", this.onKeydown);
    Object.values(this.successTimers).forEach(clearTimeout);
  },
};
</script>

<style scoped>
/* Above the nav overlay (9000) and DisplayModal (50). */
.sa-notify-host {
  position: fixed;
  top: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  width: calc(100% - 1rem);
  max-width: 26rem;
  pointer-events: none;
}

.sa-card {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  margin-bottom: 0.5rem;
  padding: 0.75rem 0.875rem;
  border: 2px solid;
  border-radius: 4px;
  background: #ffffff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -2px rgba(0, 0, 0, 0.1);
  text-align: left;
  cursor: pointer;
  font-family: Montserrat, sans-serif;
  animation: sa-enter 150ms ease-out;
}

.sa-warning {
  background: #fefce8;
  border-color: #facc15;
  color: #0c4a6e;
}
.sa-danger {
  background: #fef2f2;
  border-color: rgb(248 113 113);
  color: #0c4a6e;
}
.sa-success {
  border-color: #22c55e;
  color: #0c4a6e;
}
.sa-busy,
.sa-idle {
  border-color: #0c4a6e;
  color: #0c4a6e;
}

.sa-warning .sa-icon {
  color: #a16207;
}
.sa-danger .sa-icon {
  color: #ef4444;
}
.sa-success .sa-icon {
  color: #22c55e;
}
.sa-busy .sa-icon,
.sa-idle .sa-icon {
  color: #0c4a6e;
}

.sa-icon {
  flex: none;
  line-height: 0;
  margin-top: 1px;
}

.sa-body {
  flex: 1 1 auto;
  min-width: 0;
}

.sa-body p {
  margin: 0;
  overflow-wrap: break-word;
}

.sa-title {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
}

.sa-detail {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  margin-top: 0.125rem;
}

.sa-next {
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  margin-top: 0.25rem;
  color: #475569;
}

.sa-count {
  font-size: 13px;
  font-weight: 500;
  opacity: 0.75;
}

.sa-dismiss {
  flex: none;
  margin: -0.25rem -0.25rem 0 0;
  padding: 0 0.375rem;
  min-width: 28px;
  min-height: 28px;
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.65;
}
.sa-dismiss:hover {
  opacity: 1;
}
.sa-dismiss:focus {
  outline: 2px solid #0c4a6e;
  outline-offset: 1px;
  color: inherit;
}

@keyframes sa-enter {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sa-card {
    animation: sa-fade 150ms ease-out;
  }
  @keyframes sa-fade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
}
</style>
